import { loadLogoDataUrl, type ProBranding } from "@/hooks/use-pro-branding";

export type PdfSection = {
  title?: string;
  rows: { label: string; value: string }[];
};

export type BrandedPdfInput = {
  branding: ProBranding | null;
  /** Title shown at the top of the document (after the brand header). */
  title: string;
  /** Optional intro paragraph below the title. */
  intro?: string;
  sections: PdfSection[];
  /** Optional free text appended at the bottom (before footer). */
  notes?: string;
  /** File name without .pdf */
  fileName: string;
};

const PAGE_W = 210; // A4 mm
const PAGE_H = 297;
const MARGIN = 18;

export async function generateBrandedPdf(input: BrandedPdfInput) {
  const { branding, title, intro, sections, notes, fileName } = input;
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  let y = MARGIN;

  // ---- Header (brand) ----
  const logoDataUrl = await loadLogoDataUrl(branding?.logo_url ?? null);
  if (logoDataUrl) {
    try {
      const fmt = logoDataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
      doc.addImage(logoDataUrl, fmt as any, MARGIN, y, 28, 28, undefined, "FAST");
    } catch {
      /* ignore broken logo */
    }
  }

  const headerLeft = logoDataUrl ? MARGIN + 32 : MARGIN;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(20, 20, 20);
  doc.text(branding?.company_name || "MEI Toolbox", headerLeft, y + 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  const headerLines: string[] = [];
  if (branding?.cnpj) headerLines.push(`CNPJ: ${branding.cnpj}`);
  if (branding?.email) headerLines.push(branding.email);
  if (branding?.phone) headerLines.push(branding.phone);
  if (branding?.website) headerLines.push(branding.website);
  if (branding?.address) headerLines.push(branding.address);
  headerLines.forEach((line, i) => {
    doc.text(line, headerLeft, y + 12 + i * 4.2);
  });

  y = Math.max(y + 32, y + 14 + headerLines.length * 4.2);

  // Divider
  doc.setDrawColor(220, 220, 220);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 8;

  // ---- Title ----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(20, 20, 20);
  doc.text(title, MARGIN, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(110, 110, 110);
  doc.text(`Gerado em ${new Date().toLocaleDateString("pt-BR")}`, MARGIN, y);
  y += 8;

  if (intro) {
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const wrapped = doc.splitTextToSize(intro, PAGE_W - MARGIN * 2);
    doc.text(wrapped, MARGIN, y);
    y += wrapped.length * 5 + 4;
  }

  // ---- Sections ----
  const ensureSpace = (need: number) => {
    if (y + need > PAGE_H - 25) {
      doc.addPage();
      y = MARGIN;
    }
  };

  for (const section of sections) {
    ensureSpace(14);
    if (section.title) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(20, 20, 20);
      doc.text(section.title, MARGIN, y);
      y += 6;
      doc.setDrawColor(235, 235, 235);
      doc.line(MARGIN, y - 2, PAGE_W - MARGIN, y - 2);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    for (const row of section.rows) {
      ensureSpace(7);
      doc.setTextColor(110, 110, 110);
      doc.text(row.label, MARGIN, y);
      doc.setTextColor(20, 20, 20);
      doc.setFont("helvetica", "bold");
      const valueWrapped = doc.splitTextToSize(String(row.value), PAGE_W - MARGIN * 2 - 70);
      doc.text(valueWrapped, MARGIN + 70, y);
      doc.setFont("helvetica", "normal");
      y += Math.max(5.5, valueWrapped.length * 5);
    }
    y += 4;
  }

  if (notes) {
    ensureSpace(14);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    const wrapped = doc.splitTextToSize(notes, PAGE_W - MARGIN * 2);
    doc.text(wrapped, MARGIN, y);
    y += wrapped.length * 4.5;
  }

  // ---- Footer on every page ----
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(230, 230, 230);
    doc.line(MARGIN, PAGE_H - 18, PAGE_W - MARGIN, PAGE_H - 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    const footer =
      branding?.footer_note?.trim() ||
      "Documento gerado pelo MEI Toolbox Pro — meitoolbox.lovable.app";
    doc.text(footer, MARGIN, PAGE_H - 12);
    doc.text(`Página ${i} de ${totalPages}`, PAGE_W - MARGIN, PAGE_H - 12, { align: "right" });
  }

  const finalName = `${fileName}-${Date.now()}.pdf`;
  const blob = doc.output("blob");
  downloadBlob(blob, finalName);
}

function isAndroidWebView(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Android WebView UA contains "; wv)" — TWA/Chrome does not.
  return /Android/.test(ua) && /; wv\)/.test(ua);
}

function downloadBlob(blob: Blob, fileName: string) {
  try {
    const url = URL.createObjectURL(blob);

    // Android WebView: <a download> is ignored unless the host app
    // wires up DownloadListener. Open in a new tab/system viewer so
    // the user can save/share from there.
    if (isAndroidWebView()) {
      const opened = window.open(url, "_blank");
      if (!opened) {
        // Pop-up blocked — fall back to navigating current tab.
        window.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch (err) {
    console.error("downloadBlob failed", err);
    // Last resort: open the blob in a new window.
    try {
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      /* noop */
    }
  }
}
